import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2, RefreshCw, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import ixenSystemService, {
  type YomiLogResponse,
  type YomiLogStatsResponse,
  type YomiLogRow,
} from '@/services/ixen_system.service'
import { useAuthStore } from '@/stores/auth.store'

const PAGE_SIZE = 100

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch { return iso }
}

const fmtNum = (n: number | null, dec = 2): string =>
  n == null ? '—' : Number(n).toFixed(dec)

const decisionBadge = (d: string | null): string => {
  switch ((d ?? '').toUpperCase()) {
    case 'ALLOW':     return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    case 'BLOCK':     return 'bg-red-500/15 text-red-400 border-red-500/30'
    case 'SHADOW':    return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    case 'NO_SIGNAL': return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
    default:          return 'bg-slate-500/15 text-slate-300 border-slate-500/30'
  }
}

const labelBadge = (l: string | null): string => {
  switch ((l ?? '').toUpperCase()) {
    case 'PREMIUM':  return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    case 'STANDARD': return 'bg-sky-500/15 text-sky-400 border-sky-500/30'
    case 'CAUTION':  return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    default:         return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
  }
}

const dirCls = (d: string | null): string =>
  d === 'BUY' ? 'text-sky-400' : d === 'SELL' ? 'text-rose-400' : 'text-slate-400'

export const YomiLog = () => {
  // Filters
  const [decision, setDecision]       = useState<string>('all')
  const [signalLabel, setSignalLabel] = useState<string>('all')
  const [wasExec, setWasExec]         = useState<string>('all')   // 'all' | 'true' | 'false'
  const [direction, setDirection]     = useState<string>('all')
  const [hour, setHour]               = useState<string>('all')   // '0'..'23' | 'all'
  const [statsDays, setStatsDays]     = useState<number>(7)
  const [page, setPage]               = useState<number>(0)

  // Data
  const [stats, setStats]   = useState<YomiLogStatsResponse | null>(null)
  const [rows, setRows]     = useState<YomiLogResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadingJson, setDownloadingJson] = useState(false)

  const token = useAuthStore(s => s.token)

  const filterParams = useMemo(() => ({
    decision:     decision    !== 'all' ? decision     : undefined,
    signal_label: signalLabel !== 'all' ? signalLabel  : undefined,
    was_executed: wasExec     !== 'all' ? wasExec === 'true' : undefined,
    direction:    direction   !== 'all' ? direction    : undefined,
    hour:         hour        !== 'all' ? Number(hour) : undefined,
  }), [decision, signalLabel, wasExec, direction, hour])

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const [s, r] = await Promise.all([
        ixenSystemService.getYomiLogStats(statsDays),
        ixenSystemService.getYomiLog({
          ...filterParams,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        }),
      ])
      setStats(s); setRows(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore di caricamento')
    } finally {
      setLoading(false)
    }
  }

  // Reset page quando i filtri cambiano
  useEffect(() => { setPage(0) }, [decision, signalLabel, wasExec, direction, hour])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, signalLabel, wasExec, direction, hour, page, statsDays])

  const handleDownloadCsv = async () => {
    setDownloading(true)
    try {
      const url = ixenSystemService.buildYomiLogCsvUrl(filterParams)
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) throw new Error(`download ${r.status}`)
      const blob = await r.blob()
      const a = document.createElement('a')
      const objectUrl = URL.createObjectURL(blob)
      a.href = objectUrl
      a.download = `yomi_signal_log_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'')}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      alert('Errore download CSV: ' + (e instanceof Error ? e.message : 'unknown'))
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadJson = async () => {
    setDownloadingJson(true)
    try {
      const url = ixenSystemService.buildYomiLogJsonUrl(filterParams)
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) throw new Error(`download ${r.status}`)
      const blob = await r.blob()
      const a = document.createElement('a')
      const objectUrl = URL.createObjectURL(blob)
      a.href = objectUrl
      a.download = `yomi_export_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'')}.json`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      alert('Errore download JSON: ' + (e instanceof Error ? e.message : 'unknown'))
    } finally {
      setDownloadingJson(false)
    }
  }

  const totalPages   = rows ? Math.max(1, Math.ceil(rows.total / PAGE_SIZE)) : 1
  const allowCount   = stats?.by_decision['ALLOW'] ?? 0
  const blockCount   = stats?.by_decision['BLOCK'] ?? 0
  const shadowCount  = stats?.by_decision['SHADOW'] ?? 0
  const allowExecRate = stats && stats.total > 0
    ? ((stats.executed / Math.max(1, allowCount)) * 100).toFixed(1)
    : '—'

  return (
    <div className="space-y-4">
      {/* ── Box statistiche ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Finestra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={String(statsDays)} onValueChange={v => setStatsDays(Number(v))}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Ultime 24h</SelectItem>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                <SelectItem value="180">Ultimi 180 giorni</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Totale segnali</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats?.total ?? '—'}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-emerald-500">ALLOW</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-400">{allowCount}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {stats?.executed ?? 0} eseguiti ({allowExecRate}%)
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-red-500">BLOCK</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-red-400">{blockCount}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-amber-500">SHADOW</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-amber-400">{shadowCount}</div></CardContent>
        </Card>
      </div>

      {/* ── Filtri ─────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Decision</label>
              <Select value={decision} onValueChange={setDecision}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="ALLOW">ALLOW</SelectItem>
                  <SelectItem value="BLOCK">BLOCK</SelectItem>
                  <SelectItem value="SHADOW">SHADOW</SelectItem>
                  <SelectItem value="NO_SIGNAL">NO_SIGNAL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Qualità</label>
              <Select value={signalLabel} onValueChange={setSignalLabel}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="PREMIUM">PREMIUM</SelectItem>
                  <SelectItem value="STANDARD">STANDARD</SelectItem>
                  <SelectItem value="CAUTION">CAUTION</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Eseguito</label>
              <Select value={wasExec} onValueChange={setWasExec}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="true">Sì</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Direzione</label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="BUY">BUY</SelectItem>
                  <SelectItem value="SELL">SELL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Ora UTC</label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutte</SelectItem>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2,'0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={fetchData} variant="outline" size="sm" disabled={loading} className="flex-1">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
              <Button onClick={handleDownloadCsv} variant="outline" size="sm" disabled={downloading} className="flex-1">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                CSV
              </Button>
              <Button onClick={handleDownloadJson} variant="outline" size="sm" disabled={downloadingJson} className="flex-1" title="Esporta nello schema JSON unico">
                {downloadingJson ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                JSON
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabella ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Log Yomi v3
            {rows && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                — {rows.total.toLocaleString('it-IT')} risultati
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground" style={{fontVariantNumeric:'tabular-nums'}}>
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page + 1 >= totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-3 mb-3">
              {error}
            </div>
          )}
          {loading && !rows ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-2 pr-2 font-medium">Quando (locale)</th>
                    <th className="py-2 pr-2 font-medium">Decision</th>
                    <th className="py-2 pr-2 font-medium">Dir</th>
                    <th className="py-2 pr-2 font-medium">Qualità</th>
                    <th className="py-2 pr-2 font-medium">Reco</th>
                    <th className="py-2 pr-2 font-medium">Score</th>
                    <th className="py-2 pr-2 font-medium">Conf</th>
                    <th className="py-2 pr-2 font-medium">Regime</th>
                    <th className="py-2 pr-2 font-medium">Bias</th>
                    <th className="py-2 pr-2 font-medium">ADX H1/M15/M5</th>
                    <th className="py-2 pr-2 font-medium">Fase</th>
                    <th className="py-2 pr-2 font-medium" title="PRIME HTF Override: PRIME sbloccato perché ADX H1 ≥ 40">PRIME★</th>
                    <th className="py-2 pr-2 font-medium">Ora</th>
                    <th className="py-2 pr-2 font-medium">Prezzo</th>
                    <th className="py-2 pr-2 font-medium">Eseg.</th>
                    <th className="py-2 pr-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows?.items.length === 0 && (
                    <tr><td colSpan={16} className="py-8 text-center text-muted-foreground">
                      Nessun log trovato per i filtri correnti.
                    </td></tr>
                  )}
                  {rows?.items.map((r: YomiLogRow) => (
                    <tr key={r.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground" style={{fontVariantNumeric:'tabular-nums'}}>
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Badge variant="outline" className={`${decisionBadge(r.decision)} text-[10px]`}>
                          {r.decision ?? '—'}
                        </Badge>
                      </td>
                      <td className={`py-1.5 pr-2 font-bold ${dirCls(r.direction)}`}>
                        {r.direction === 'BUY' ? '▲ BUY' : r.direction === 'SELL' ? '▼ SELL' : '—'}
                      </td>
                      <td className="py-1.5 pr-2">
                        {r.signal_label && (
                          <Badge variant="outline" className={`${labelBadge(r.signal_label)} text-[10px]`}>
                            {r.signal_label}
                          </Badge>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground">
                        {r.recommendation_level ?? '—'}
                      </td>
                      <td className="py-1.5 pr-2" style={{fontVariantNumeric:'tabular-nums'}}>
                        {fmtNum(r.recommendation_score, 1)}
                      </td>
                      <td className="py-1.5 pr-2" style={{fontVariantNumeric:'tabular-nums'}}>
                        {fmtNum(r.confidence, 2)}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground text-[11px]">{r.regime ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-muted-foreground text-[11px]">{r.bias ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-muted-foreground text-[11px]" style={{fontVariantNumeric:'tabular-nums'}}>
                        {fmtNum(r.adx_h1, 1)} / {fmtNum(r.adx_m15, 1)} / {fmtNum(r.adx_m5, 1)}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground text-[11px]">{r.adx_phase ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-center" title={r.prime_htf_override ? 'PRIME sbloccato (HTF forte: ADX H1 ≥ 40)' : ''}>
                        {r.prime_htf_override
                          ? <span className="text-amber-400 font-bold">★</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground" style={{fontVariantNumeric:'tabular-nums'}}>
                        {r.hour_utc != null ? `${String(r.hour_utc).padStart(2,'0')}h` : '—'}
                      </td>
                      <td className="py-1.5 pr-2" style={{fontVariantNumeric:'tabular-nums'}}>
                        {r.reference_price != null ? `$${fmtNum(r.reference_price, 2)}` : '—'}
                      </td>
                      <td className="py-1.5 pr-2 text-center">
                        {r.was_executed
                          ? <span className="text-emerald-400 font-bold">✓</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground text-[11px] max-w-[200px] truncate" title={r.reason ?? ''}>
                        {r.reason ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
